'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import { formatUsdFromCents } from '@/hooks/useTomVendingReports';
import {
  fetchTomVendingSiteVisitHistoryByRange,
  type TomVendingSiteVisitHistoryRecentRow,
} from '@/hooks/useTomVendingSiteVisitHistory';
import { useNotification } from '@/contexts/NotificationContext';
import { useTomVendingLocationsList, tomVendingLocationIsWarehouse } from '@/hooks/useTomVendingLocation';
import { formatVisitDateLabel } from '../toms-vending/TomsVendingSiteVisitDetailContent';
import { downloadSiteVisitsXlsx, TOMS_REPORT_EXPORT_MAX_ROWS } from '@/utils/tomsVendingReportExport';
import { fetchAllReportRows } from '@/utils/downloadXlsx';

const PAGE_SIZE = 50;

function defaultDateRange() {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  };
}

type LocRow = { id: number; name?: string; isWarehouse?: boolean };

const TomsVendingSiteVisitReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { data: locations = [], isLoading: locLoading } = useTomVendingLocationsList();

  const init = useMemo(() => defaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState(init.dateFrom);
  const [dateTo, setDateTo] = useState(init.dateTo);
  const [locationId, setLocationId] = useState<number | ''>('');
  const [data, setData] = useState<TomVendingSiteVisitHistoryRecentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await fetchTomVendingSiteVisitHistoryByRange({
        dateFrom,
        dateTo,
        locationTomVendingId:
          locationId === '' || Number.isNaN(Number(locationId)) ? undefined : Number(locationId),
        skip: pageIndex * PAGE_SIZE,
        take: PAGE_SIZE,
      });
      setData(r.data);
      setTotal(r.total);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load site visits');
      setData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo, locationId, pageIndex, showError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleDownloadExcel = useCallback(async () => {
    setExporting(true);
    try {
      const locationTomVendingId =
        locationId === '' || Number.isNaN(Number(locationId)) ? undefined : Number(locationId);
      const { rows } = await fetchAllReportRows({
        pageSize: 200,
        maxRows: TOMS_REPORT_EXPORT_MAX_ROWS,
        fetchPage: async (skip, take) => {
          const r = await fetchTomVendingSiteVisitHistoryByRange({
            dateFrom,
            dateTo,
            locationTomVendingId,
            skip,
            take,
          });
          return { rows: r.data, total: r.total };
        },
      });
      if (rows.length === 0) {
        showError('Nothing to export for the selected date range.');
        return;
      }
      downloadSiteVisitsXlsx(rows, dateFrom, dateTo);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to download Excel');
    } finally {
      setExporting(false);
    }
  }, [dateFrom, dateTo, locationId, showError]);

  const locOpts = locations as LocRow[];
  const selectedLoc = locationId === '' ? null : locOpts.find((l) => l.id === locationId) ?? null;

  const openVisit = (row: TomVendingSiteVisitHistoryRecentRow) => {
    navigate(
      `/app/toms-vending/site-visits/${row.siteVisitId}?locationId=${row.locationTomVendingId}`,
    );
  };

  const columns = useMemo<MRT_ColumnDef<TomVendingSiteVisitHistoryRecentRow>[]>(
    () => [
      {
        accessorKey: 'visitDate',
        header: 'Visit date',
        size: 160,
        Cell: ({ cell }) => formatVisitDateLabel(String(cell.getValue() ?? '')),
      },
      {
        accessorKey: 'locationName',
        header: 'Location',
        size: 200,
        Cell: ({ row }) => row.original.locationName?.trim() || `Location ${row.original.locationTomVendingId}`,
      },
      {
        accessorKey: 'userDisplayName',
        header: 'User',
        size: 160,
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      {
        accessorKey: 'machineSnapshotCount',
        header: 'Machines',
        size: 100,
        Cell: ({ cell }) => String(cell.getValue() ?? 0),
      },
      {
        id: 'cashCollected',
        header: 'Cash collected',
        size: 120,
        Cell: ({ row }) => formatUsdFromCents(row.original.totalCashCollectedCents ?? 0),
      },
      {
        accessorKey: 'siteVisitId',
        header: 'Visit #',
        size: 90,
      },
    ],
    [],
  );

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Reports', href: '/app/toms-vending/reports' },
    { label: 'Site visits' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="Site visits report" />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Saved site visit snapshots with machine tallies. Filter by visit date range and optionally
          narrow to one location.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPageIndex(0);
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPageIndex(0);
            }}
          />
          <Autocomplete<LocRow>
            size="small"
            sx={{ minWidth: 260 }}
            options={locOpts}
            loading={locLoading}
            value={selectedLoc}
            onChange={(_, v) => {
              setLocationId(v?.id ?? '');
              setPageIndex(0);
            }}
            getOptionLabel={(l) => l.name?.trim() || `Location ${l.id}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, l) => (
              <li {...props} key={l.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{l.name?.trim() || `Location ${l.id}`}</Typography>
                  {tomVendingLocationIsWarehouse(l) ? (
                    <Chip label="Warehouse" size="small" variant="outlined" color="secondary" />
                  ) : null}
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Location (optional)" />}
          />
          <Button variant="contained" size="small" onClick={() => void fetchData()} disabled={isLoading}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleDownloadExcel()}
            disabled={isLoading || exporting || total === 0}
          >
            {exporting ? 'Downloading…' : 'Download Excel'}
          </Button>
          <Button
            component={RouterLink}
            to="/app/toms-vending/reports"
            variant="text"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            All reports
          </Button>
        </Box>

        <MaterialReactTable
          columns={columns}
          data={data}
          manualPagination
          rowCount={total}
          enableGlobalFilter={false}
          onPaginationChange={(updater) => {
            setPageIndex(
              typeof updater === 'function'
                ? updater({ pageIndex, pageSize: PAGE_SIZE }).pageIndex
                : updater.pageIndex,
            );
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => openVisit(row.original),
            sx: { cursor: 'pointer' },
          })}
          state={{
            isLoading,
            pagination: { pageIndex, pageSize: PAGE_SIZE },
          }}
          initialState={{ density: 'compact' }}
        />
      </Box>
    </Box>
  );
};

export default TomsVendingSiteVisitReportPage;
